import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import Masonry from 'react-masonry-css';
import { useHistory } from 'react-router';
import { useQuery } from 'utils/useQuery';
import { DateRangeSelection } from './DateRangeSelection';
import { SortableItem } from './SortableItem';
import { StoreMetricsAddMetric } from './StoreMetricsAddMetric';
import { StoreMetricsGroupByMenu } from './StoreMetricsGroupByMenu';
import { useStoreMetrics } from './useStoreMetrics';

export const StoreMetrics = () => {
  const {
    selectedMetrics,
    setSelectedMetrics,
    expandAllCards,
    handleExpandAllCards,
    handleSaveMetricsToLocalStorage,
  } = useStoreMetrics();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const query = useQuery();
  const history = useHistory();

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = selectedMetrics.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = selectedMetrics.findIndex((item) => item.id === over.id);
      const newSelectedMetrics = arrayMove(selectedMetrics, oldIndex, newIndex);

      setSelectedMetrics(newSelectedMetrics);

      query.set('metrics', newSelectedMetrics.map((item) => item.id).join(','));
      history.push({
        search: query.toString(),
      });

      handleSaveMetricsToLocalStorage(newSelectedMetrics);
    }
  }

  const hasExpandadedCard = selectedMetrics.find(
    (item) => item.expandedCard === true
  );

  return (
    <section className="mt-7 md:mt-16">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <p className="text-base tracking-wide md:text-2xl ">Store metrics</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex text-sm divide-x ">
            <div className="px-4">
              <button
                className={` hover:text-segments-accent-color-1 ${
                  expandAllCards
                    ? 'text-segments-link font-bold'
                    : 'border-b border-dotted'
                }`}
                onClick={() => handleExpandAllCards({ state: true })}
              >
                Expand
              </button>
            </div>
            <div className="pl-4">
              <button
                className={`hover:text-segments-accent-color-1 ${
                  !!!expandAllCards && !!!hasExpandadedCard
                    ? 'text-segments-link font-bold'
                    : 'border-b border-dotted'
                }`}
                onClick={() => handleExpandAllCards({ state: false })}
              >
                Collapse
              </button>
            </div>
          </div>
          <StoreMetricsGroupByMenu />
          <DateRangeSelection />
          <StoreMetricsAddMetric />
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={selectedMetrics} strategy={rectSortingStrategy}>
          <div className="mt-6">
            <Masonry
              breakpointCols={{
                default: 2,
                600: 1,
              }}
              className="flex space-x-6 "
              columnClassName="my-masonry-grid_column bg-clip-padding space-y-6"
            >
              {selectedMetrics.map((metric) => (
                <SortableItem key={metric.id} metric={metric} />
              ))}
            </Masonry>
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
};
